import { useEffect, useState } from 'react';

import { formatCount } from '../candidate-ai/candidateAiSuggestions.formatters';
import {
  CandidateAiReviewAction,
  CandidateAiReviewNotice,
  CandidateAiReviewResponse,
  formatCandidateAiReviewActionValue,
  formatCandidateAiReviewFailedItem,
  getCandidateAiReviewCvHash,
  getCandidateAiSuggestionFieldLabel,
  mapCandidateAiReviewError,
  useCandidateAiReviewActions,
} from '../candidate-ai/useCandidateAiReviewActions';
import { CandidateType } from '../candidates/Candidates.types';

export const GLOBAL_ACCEPT_ALL_KEY = 'global:accept-all';
const SECTION_ACCEPT_ALL_SUFFIX = ':accept-all';

export const sectionAcceptAllKey = (field: string) => `set:${field}${SECTION_ACCEPT_ALL_SUFFIX}`;
const isSectionAcceptAllKey = (reviewKey: string) => reviewKey.endsWith(SECTION_ACCEPT_ALL_SUFFIX);

const isAcceptAction = (actionType: CandidateAiReviewAction['type']) =>
  actionType === 'ACCEPT_SCALAR' || actionType === 'ACCEPT_SET_ITEM';

const getBatchFieldLabel = (actions: CandidateAiReviewAction[]) => {
  const firstAction = actions[0];

  if (!firstAction || !actions.every((action) => action.field === firstAction.field)) {
    return null;
  }

  return getCandidateAiSuggestionFieldLabel(firstAction.field);
};

// Short past-tense confirmation for the status line, e.g. "Accepted City" or "Accepted all technologies".
const getSuccessFeedback = (actions: CandidateAiReviewAction[], reviewKey: string) => {
  const [firstAction] = actions;

  if (!firstAction) {
    return 'Reviewed';
  }

  if (reviewKey === GLOBAL_ACCEPT_ALL_KEY) {
    return `Accepted ${formatCount(actions.length, 'suggestion')}`;
  }

  if (isSectionAcceptAllKey(reviewKey)) {
    const fieldLabel = getBatchFieldLabel(actions);
    return fieldLabel
      ? `Accepted all ${fieldLabel.toLowerCase()}`
      : `Accepted ${formatCount(actions.length, 'suggestion')}`;
  }

  const reviewValue = formatCandidateAiReviewActionValue(firstAction);
  return isAcceptAction(firstAction.type) ? `Accepted ${reviewValue}` : `Rejected ${reviewValue}`;
};

const FEEDBACK_DURATION_MS = 4000;

const getErrorHeadline = (actions: CandidateAiReviewAction[], reviewKey: string) => {
  const [firstAction] = actions;

  if (!firstAction) {
    return 'Could not review CV suggestions.';
  }

  if (reviewKey === GLOBAL_ACCEPT_ALL_KEY) {
    return 'Could not accept all CV suggestions.';
  }

  if (isSectionAcceptAllKey(reviewKey)) {
    const fieldLabel = getBatchFieldLabel(actions);
    return fieldLabel
      ? `Could not accept all CV suggestions for ${fieldLabel}.`
      : 'Could not accept the selected CV suggestions.';
  }

  const reviewValue = formatCandidateAiReviewActionValue(firstAction);
  return isAcceptAction(firstAction.type)
    ? `Could not accept CV suggestion for ${reviewValue}.`
    : `Could not reject CV suggestion for ${reviewValue}.`;
};

const getReviewResultSummary = (appliedCount: number, failedCount: number) => {
  if (appliedCount > 0 && failedCount > 0) {
    return `${formatCount(appliedCount, 'suggestion')} applied, ${failedCount} failed.`;
  }

  if (failedCount > 0) {
    return `No CV suggestions were applied. ${failedCount} failed.`;
  }

  return `${formatCount(appliedCount, 'suggestion')} applied.`;
};

const buildReviewNoticeFromResponse = (
  actions: CandidateAiReviewAction[],
  response: CandidateAiReviewResponse
): CandidateAiReviewNotice | null => {
  if (response.failedCount === 0) {
    return null;
  }

  const failedItems = response.results.reduce<string[]>((acc, result) => {
    const action = actions[result.index];
    if (result.status === 'FAILED' && action) {
      acc.push(formatCandidateAiReviewFailedItem(action, result.errorCode));
    }
    return acc;
  }, []);

  return {
    kind: response.appliedCount > 0 ? 'partial-success' : 'partial-failure',
    message: getReviewResultSummary(response.appliedCount, response.failedCount),
    details: failedItems.slice(0, 5),
    hiddenDetailsCount: Math.max(failedItems.length - 5, 0),
  };
};

type UseCandidateCvSuggestionsReviewProps = {
  candidate?: CandidateType;
  pendingCount: number;
  refresh: () => Promise<unknown>;
};

export const useCandidateCvSuggestionsReview = ({
  candidate,
  pendingCount,
  refresh,
}: UseCandidateCvSuggestionsReviewProps) => {
  const reviewMutation = useCandidateAiReviewActions();
  const [reviewNotice, setReviewNotice] = useState<CandidateAiReviewNotice | null>(null);
  const [activeReviewKey, setActiveReviewKey] = useState<string | null>(null);
  const [activeReviewAction, setActiveReviewAction] = useState<CandidateAiReviewAction | null>(null);
  // Whether the last successful review started with pending items; "all reviewed" means it cleared them.
  const [lastSuccessHadPending, setLastSuccessHadPending] = useState(false);
  const [feedback, setFeedback] = useState<{ id: number; message: string } | null>(null);

  // A different candidate starts with a clean review session.
  useEffect(() => {
    setReviewNotice(null);
    setLastSuccessHadPending(false);
    setFeedback(null);
  }, [candidate?.uuid]);

  useEffect(() => {
    if (!feedback) return;
    const timeout = window.setTimeout(() => setFeedback(null), FEEDBACK_DURATION_MS);
    return () => window.clearTimeout(timeout);
  }, [feedback]);

  const review = (actions: CandidateAiReviewAction[], reviewKey: string) => {
    const cvHash = candidate && getCandidateAiReviewCvHash(candidate);

    if (
      !candidate ||
      !cvHash ||
      !actions.length ||
      !candidate.cv ||
      candidate.aiSuggestions.basedOnCurrentCv === false
    ) {
      return Promise.resolve();
    }

    const hadPending = pendingCount > 0;
    setReviewNotice(null);
    setFeedback(null);
    setActiveReviewKey(reviewKey);
    setActiveReviewAction(actions.length === 1 ? actions[0] ?? null : null);

    return reviewMutation
      .mutateAsync({ candidateId: candidate.uuid, cvHash, actions })
      .then(async (response) => {
        // Partial and failed batches explain themselves in the inline notice; full success gets a quiet line.
        setReviewNotice(buildReviewNoticeFromResponse(actions, response));
        if (response.failedCount === 0) {
          setFeedback({ id: Date.now(), message: getSuccessFeedback(actions, reviewKey) });
        }

        setLastSuccessHadPending(response.appliedCount > 0 && hadPending);
        await refresh();
      })
      .catch(async (error) => {
        setLastSuccessHadPending(false);
        const mappedError = mapCandidateAiReviewError(error);
        setReviewNotice({
          ...mappedError,
          message: getErrorHeadline(actions, reviewKey),
          details: [mappedError.message],
        });

        if (error.response?.status === 409) {
          await refresh();
        }
      })
      .finally(() => {
        setActiveReviewKey(null);
        setActiveReviewAction(null);
      });
  };

  return {
    review,
    reviewNotice,
    isReviewLoading: reviewMutation.isLoading,
    activeReviewKey,
    activeReviewAction,
    clearedPending: lastSuccessHadPending && pendingCount === 0,
    feedback: feedback?.message ?? null,
  };
};
